'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { AnimatedBigIntOnMount } from '@/components/ui/animated-bigint-on-mount';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { DropdownEntry } from '@/components/ui/app-dropdown-menu';
import { AppDropdownMenu } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon, type IconProps } from '@/components/ui/icon';
import { ArrowDown01Icon, Cash01IconSolid, MoneyBag01IconSolid } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { PnlDirectionArrow } from '@/components/ui/pnl-direction-arrow';
import { useUserPortfolioSummary } from '@/hooks/positions/user-portfolio/selectors';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
interface PortfolioCurrentSummaryProps {
  readonly userId: string;
  readonly walletPubkeys: readonly string[];
  readonly viewerMode: 'own' | 'other';
  readonly pickerTriggerLabel: string;
  readonly pickerItems: ReadonlyArray<DropdownEntry>;
  readonly showWalletPicker: boolean;
  readonly deltaD6: bigint | null;
}
export function PortfolioCurrentSummary(t0) {
  const $ = _c(30);
  const {
    userId,
    walletPubkeys,
    viewerMode,
    pickerTriggerLabel,
    pickerItems,
    showWalletPicker,
    deltaD6
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  const tPicker = useTranslations("profile.next.picker");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      userId,
      wallets: walletPubkeys
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summary = useUserPortfolioSummary(t1);
  if (summary.isPending) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex min-w-0 flex-col gap-2" data-testid="portfolio-current-summary-loading"><Skeleton className="h-3 w-12" /><Skeleton className="h-12 max-w-[min(100%,12rem)] w-48" /><Skeleton className="h-4 max-w-[min(100%,10rem)] w-40" /></div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== formatD6) {
    t2 = function formatTotal(valueD6) {
      return formatD6(valueD6.toString(), "full");
    };
    $[4] = formatD6;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const formatTotal = t2;
  let t3;
  if ($[6] !== pickerItems || $[7] !== pickerTriggerLabel || $[8] !== showWalletPicker || $[9] !== tPicker || $[10] !== viewerMode) {
    t3 = viewerMode === "own" && showWalletPicker ? <AppDropdownMenu align="start" trigger={<AnimatedButton variant="tertiary" size="xs" className="-ml-1 self-start px-1 tracking-wide hover:bg-transparent" aria-label={tPicker("trigger_label")} data-testid="portfolio-pnl-card-wallet-picker-trigger"><span className="truncate">{pickerTriggerLabel}</span><Icon icon={ArrowDown01Icon} className="size-3.5" aria-hidden="true" /></AnimatedButton>} items={pickerItems} /> : <span className="-ml-1 self-start truncate px-1 text-caption tracking-wide text-foreground-secondary" data-testid="portfolio-pnl-card-wallet-picker-label">{pickerTriggerLabel}</span>;
    $[6] = pickerItems;
    $[7] = pickerTriggerLabel;
    $[8] = showWalletPicker;
    $[9] = tPicker;
    $[10] = viewerMode;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t) {
    t4 = t("total_label");
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== formatTotal || $[15] !== summary.totalD6) {
    t5 = summary.totalD6 === null ? <span className="text-display-sm font-medium tracking-tight text-foreground-primary">{formatTotal(BigInt(0))}</span> : <AnimatedBigInt value={summary.totalD6} format={formatTotal} className="text-display-sm font-medium tracking-tight text-foreground-primary" />;
    $[14] = formatTotal;
    $[15] = summary.totalD6;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <fieldset className="m-0 flex w-max items-baseline gap-0.5 whitespace-nowrap border-0 p-0" data-testid="portfolio-pnl-card-total" aria-label={t4}>{t5}</fieldset>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== deltaD6 || $[21] !== formatD6 || $[22] !== formatD6Signed) {
    t7 = deltaD6 !== null ? <fieldset className={cn("m-0 inline-flex min-w-0 w-fit items-center gap-1.5 rounded-pill border-0 py-1 pl-1.5 pr-3 text-body-lg font-medium tabular-nums transition-colors", deltaToneClass(deltaD6), deltaD6 < BigInt(0) ? "bg-data-loss/10" : deltaD6 > BigInt(0) ? "bg-data-profit/10" : "bg-background-tertiary")} data-testid="portfolio-pnl-card-delta" aria-label={formatD6Signed(deltaD6.toString(), "full")}>{deltaD6 === BigInt(0) ? null : <PnlDirectionArrow valueD6={deltaD6} className="size-5" />}<AnimatedBigInt value={deltaD6 < BigInt(0) ? -deltaD6 : deltaD6} format={valueD6_0 => formatD6(valueD6_0.toString(), "full")} /></fieldset> : null;
    $[20] = deltaD6;
    $[21] = formatD6;
    $[22] = formatD6Signed;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t7) {
    t8 = <div className="min-h-8">{t7}</div>;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t3 || $[27] !== t6 || $[28] !== t8) {
    t9 = <div className="flex min-w-0 flex-col gap-2">{t3}{t6}{t8}</div>;
    $[26] = t3;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
export function PortfolioBalanceTiles(t0) {
  const $ = _c(23);
  const {
    userId,
    walletPubkeys
  } = t0;
  const tStats = useTranslations("profile.stats");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      userId,
      wallets: walletPubkeys
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summary = useUserPortfolioSummary(t1);
  if (summary.isPending) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="grid grid-cols-2 gap-2"><StatTileSkeleton /><StatTileSkeleton /></div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== tStats) {
    t2 = tStats("cash");
    $[4] = tStats;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== formatD6) {
    t3 = valueD6 => formatD6(valueD6.toString(), "full");
    $[6] = formatD6;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== summary.cashD6 || $[9] !== t2 || $[10] !== t3) {
    t4 = <StatTile testId="stat-tile-cash" label={t2} icon={Cash01IconSolid} iconColorClassName="text-feedback-success-content" value={summary.cashD6} format={t3} />;
    $[8] = summary.cashD6;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== tStats) {
    t5 = tStats("holdings");
    $[12] = tStats;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== formatD6) {
    t6 = valueD6_0 => formatD6(valueD6_0.toString(), "compact");
    $[14] = formatD6;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== summary.holdingsD6 || $[17] !== t5 || $[18] !== t6) {
    t7 = <StatTile testId="stat-tile-holdings" label={t5} icon={MoneyBag01IconSolid} iconColorClassName="text-feedback-info-content" value={summary.holdingsD6} format={t6} />;
    $[16] = summary.holdingsD6;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t7) {
    t8 = <div className="grid grid-cols-2 gap-2">{t4}{t7}</div>;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function StatTile(t0) {
  const $ = _c(19);
  const {
    label,
    value,
    format,
    testId,
    icon,
    iconColorClassName
  } = t0;
  let t1;
  if ($[0] !== iconColorClassName) {
    t1 = cn("size-5", iconColorClassName);
    $[0] = iconColorClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon || $[3] !== t1) {
    t2 = <Icon icon={icon} className={t1} aria-hidden="true" />;
    $[2] = icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label) {
    t3 = <span className="text-caption text-foreground-tertiary">{label}</span>;
    $[5] = label;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== format || $[8] !== value) {
    t4 = value === null ? <span className="text-heading-md font-medium tabular-nums text-foreground-primary">{format(BigInt(0))}</span> : <AnimatedBigIntOnMount value={value} format={format} className="text-heading-md font-medium text-foreground-primary" />;
    $[7] = format;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex min-w-0 flex-col gap-0.5">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t5) {
    t6 = <div className="flex flex-col gap-2.5 p-4">{t2}{t5}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6 || $[17] !== testId) {
    t7 = <Panel density="flush" className="min-w-0" data-testid={testId}>{t6}</Panel>;
    $[16] = t6;
    $[17] = testId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function StatTileSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="size-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Panel density="flush" className="min-w-0"><div className="flex flex-col gap-2.5 p-4">{t0}<div className="flex flex-col gap-0.5"><Skeleton className="h-3 w-12" /><Skeleton className="h-7 w-16 max-w-full" /></div></div></Panel>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function deltaToneClass(deltaD6: bigint): string {
  if (deltaD6 > BigInt(0)) return 'text-data-profit';
  if (deltaD6 < BigInt(0)) return 'text-data-loss';
  return 'text-foreground-tertiary';
}